/**
 * Rotation events: the facts recorded on swap days. The log is append-only.
 * A mistake is fixed by recording `event.voided`, never by editing an earlier event.
 *
 * The log records what HAPPENED. A plan (what is intended) is a separate concept and never
 * produces events by itself. A refused pot is simply a log entry that differs from the plan.
 */
import { createEventUpgrader, type EventEnvelope } from "@hitl/core";
import { isCalendarDate, type CalendarDate } from "./calendar";

export interface PotAtSlot {
  readonly potId: string;
  readonly clientId: string;
  readonly slotId: string;
  readonly on: CalendarDate;
}

export interface PotPlaced extends PotAtSlot {
  /** Present when she placed the pot despite a red verdict (e.g. the client asked for a favourite back). */
  readonly override?: { readonly reason: string };
}
export type PotCollected = PotAtSlot;
export interface PotRejected extends PotAtSlot {
  /** Why the client didn't want it (colour, size, ...): feeds future client preferences. */
  readonly reason?: string;
}
/** Why a pot left the business. Kept as a closed list so reports and tax exports group cleanly. */
export const WRITE_OFF_REASONS = ["broken", "coating", "flowers", "lost", "other"] as const;
export type WriteOffReason = (typeof WRITE_OFF_REASONS)[number];

/**
 * A pot written off (broken, deteriorated, lost). Permanent evidence for the asset register and
 * tax: corrected like any fact, by voiding it, never by editing.
 */
export interface PotWrittenOff {
  readonly potId: string;
  readonly on: CalendarDate;
  readonly reason: WriteOffReason;
  readonly note?: string;
  /** Reference to a photo stored alongside the record (evidence), when she took one. */
  readonly photoId?: string;
}

export interface EventVoided {
  readonly voidsEventId: string;
  readonly reason: string;
}

export type RotationEvent =
  | EventEnvelope<"pot.placed", PotPlaced>
  | EventEnvelope<"pot.collected", PotCollected>
  | EventEnvelope<"pot.rejected", PotRejected>
  | EventEnvelope<"pot.written-off", PotWrittenOff>
  | EventEnvelope<"event.voided", EventVoided>;

/** Bump a version here and add a migration step whenever a payload shape changes. */
export const ROTATION_EVENT_VERSIONS = {
  "pot.placed": 1,
  "pot.collected": 1,
  "pot.rejected": 1,
  "pot.written-off": 1,
  "event.voided": 1,
} as const satisfies Record<RotationEvent["type"], number>;

const upgrader = createEventUpgrader(ROTATION_EVENT_VERSIONS);

const isText = (v: unknown): v is string => typeof v === "string" && v.trim().length > 0;
const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

function isPotAtSlot(p: Record<string, unknown>): boolean {
  return isText(p.potId) && isText(p.clientId) && isText(p.slotId) && isCalendarDate(p.on);
}

function validPayload(type: string, p: Record<string, unknown>): boolean {
  switch (type) {
    case "pot.placed":
      return isPotAtSlot(p) && (p.override === undefined || (isRecord(p.override) && isText(p.override.reason)));
    case "pot.collected":
      return isPotAtSlot(p);
    case "pot.rejected":
      return isPotAtSlot(p) && (p.reason === undefined || typeof p.reason === "string");
    case "pot.written-off":
      return (
        isText(p.potId) &&
        isCalendarDate(p.on) &&
        (WRITE_OFF_REASONS as readonly unknown[]).includes(p.reason) &&
        (p.note === undefined || typeof p.note === "string") &&
        (p.photoId === undefined || isText(p.photoId))
      );
    case "event.voided":
      return isText(p.voidsEventId) && isText(p.reason);
    default:
      return false;
  }
}

/**
 * Turn an envelope that arrived from a device (any build, untrusted payload) into a typed
 * rotation event at the current schema version, or explain why it can't be read.
 */
export function readRotationEvent(
  raw: EventEnvelope,
): { ok: true; event: RotationEvent } | { ok: false; reason: string } {
  const upgraded = upgrader.upgrade(raw);
  if (!upgraded.ok) return upgraded;
  const { event } = upgraded;
  if (!isRecord(event.payload) || !validPayload(event.type, event.payload)) {
    return { ok: false, reason: `malformed "${event.type}" payload` };
  }
  return { ok: true, event: event as RotationEvent };
}
